/**
 * Landing Footer Component
 * Figma: Footer (Node: 479:19826)
 *
 * Footer with mailing list signup, links, and copyright
 * Dimensions: 1280px wide
 */
"use client";

import { useState } from "react";

import Image from "next/image";

import { SUPPORT_EMAIL_COSIMO } from "@/constants/support-emails";
import {
  appendReturnTo,
  LANDING_PAGE_PATH,
  LEGAL_ACCESSIBILITY_PATH,
  LEGAL_PRIVACY_POLICY_PATH,
  LEGAL_TERMS_OF_SERVICE_PATH,
} from "@/lib/route-paths";
import { colors } from "@/styles/design-tokens";
import { SECTION_PADDING_X } from "@/styles/responsive-spacing";
import { isValidEmail } from "@/utils/validation-utils";

const FOOTER_LINKS = [
  "Contact",
  "Instagram",
  "Privacy Policy",
  "Terms of Service",
  "Careers",
  "Accessibility",
];

type MailingListSignupResponse = {
  detail?: string;
};

export function LandingFooter() {
  const [emailValue, setEmailValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error" | "invalid">(
    "idle"
  );

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValue(e.target.value);
    if (submitStatus === "invalid" || submitStatus === "error") {
      setSubmitStatus("idle");
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = emailValue.trim();

    if (!normalizedEmail || isSubmitting) return;

    if (!isValidEmail(normalizedEmail)) {
      setSubmitStatus("invalid");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/mailing-list/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as MailingListSignupResponse | null;
        throw new Error(data?.detail || "Unable to submit mailing list signup");
      }

      setSubmitStatus("success");

      setEmailValue("");

      setTimeout(() => setSubmitStatus("idle"), 3000);
    } catch (error) {
      console.error("Error submitting email:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="flex w-full justify-center bg-white"
      style={{
        borderTop: `1px solid ${colors.divider.light}`,
        paddingTop: "clamp(40px, 6vw, 64px)",
        paddingBottom: "clamp(40px, 6vw, 64px)",
      }}
    >
      <div
        className="flex w-full flex-col items-center"
        style={{ maxWidth: "1280px", gap: "40px", paddingInline: SECTION_PADDING_X.RESPONSIVE }}
      >
        <div className="flex w-full flex-col items-center" style={{ gap: "16px" }}>
          <p className="text-overline text-ink text-center">Join Our Mailing List</p>
          <p className="text-body-lg text-center text-black/50" style={{ maxWidth: "484px" }}>
            Stay updated on new brands, curated collections, and editorial discoveries
          </p>
        </div>

        <form
          onSubmit={handleEmailSubmit}
          className="flex w-full flex-col items-center gap-2 sm:max-w-[626px]"
        >
          <div className="flex w-full items-start" style={{ height: "60px" }}>
            <div
              className="relative flex-1 overflow-hidden"
              style={{
                height: "60px",
                borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
              }}
            >
              <div className="flex h-full w-full items-center">
                <div className="flex flex-1 items-center justify-between">
                  <input
                    type="email"
                    value={emailValue}
                    onChange={handleEmailChange}
                    placeholder="Enter your email address"
                    required
                    disabled={isSubmitting}
                    className="flex-1 border-0 bg-transparent outline-none"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: "normal",
                      letterSpacing: "-0.7125px",
                      color: emailValue ? colors.text.black : "rgba(0, 0, 0, 0.4)",
                      borderRadius: "0",
                      WebkitAppearance: "none",
                      padding: "0",
                    }}
                  />

                  <button
                    type="submit"
                    disabled={!emailValue || isSubmitting}
                    className="flex cursor-pointer items-center"
                    style={{
                      height: "20px",
                      paddingLeft: "8px",
                      paddingRight: "8px",
                      gap: "4px",
                      background: "none",
                      border: "none",
                      opacity: isSubmitting ? 0.5 : 1,
                    }}
                  >
                    <div className="flex items-center" style={{ gap: "8px" }}>
                      <div
                        className="flex items-center justify-center overflow-hidden"
                        style={{
                          maxWidth: emailValue ? "100px" : "0px",
                          opacity: emailValue ? 1 : 0,
                          transition: "max-width 0.3s ease-in-out, opacity 0.3s ease-in-out",
                        }}
                      >
                        <p
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: "11px",
                            fontWeight: 400,
                            lineHeight: "16.5px",
                            letterSpacing: "0.9445px",
                            textTransform: "uppercase",
                            color: colors.text.black,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isSubmitting ? "Sending..." : "Subscribe"}
                        </p>
                      </div>
                      <div style={{ width: "14px", height: "14px" }}>
                        <Image src="/icons/arrow-email.svg" alt="" width={14} height={14} />
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {submitStatus === "success" && (
            <p
              style={{
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                color: colors.text.black,
                textAlign: "center",
              }}
            >
              Check your inbox to confirm your subscription.
            </p>
          )}
          {submitStatus === "invalid" && (
            <p
              style={{
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                color: "#dc2626",
                textAlign: "center",
              }}
            >
              Please enter a valid email address.
            </p>
          )}
          {submitStatus === "error" && (
            <p
              style={{
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                color: "#dc2626",
                textAlign: "center",
              }}
            >
              Something went wrong. Please try again.
            </p>
          )}
        </form>

        <div
          className="flex w-full flex-col items-center"
          style={{
            borderTop: `1px solid ${colors.divider.light}`,
            paddingTop: "36px",
            gap: "28px",
          }}
        >
          <div className="flex w-full flex-col items-center gap-3 text-center sm:flex-row sm:justify-center sm:gap-3">
            <p className="text-overline-wide text-black/50">Cosimo is currently in beta.</p>
          </div>

          <div
            className="flex w-full flex-wrap items-center justify-center gap-4"
            style={{
              borderBottom: `1px solid ${colors.divider.light}`,
              paddingBottom: "28px",
            }}
          >
            {FOOTER_LINKS.map((label) => (
              <FooterLink key={label} label={label} />
            ))}
          </div>

          <p
            style={{
              fontSize: "10px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              lineHeight: "15px",
              letterSpacing: "-0.1328px",
              color: colors.text.grayLightest,
              textAlign: "center",
            }}
          >
            © 2026 Cosimo
          </p>
        </div>
      </div>
    </section>
  );
}

// Footer Link Component
function FooterLink({ label }: { label: string }) {
  let href: string | null = null;

  switch (label) {
    case "Contact":
      href = `mailto:${SUPPORT_EMAIL_COSIMO}`;
      break;
    case "Instagram":
      href = "https://instagram.com/shopcosimo";
      break;
    case "Privacy Policy":
      href = appendReturnTo(LEGAL_PRIVACY_POLICY_PATH, LANDING_PAGE_PATH);
      break;
    case "Terms of Service":
      href = appendReturnTo(LEGAL_TERMS_OF_SERVICE_PATH, LANDING_PAGE_PATH);
      break;
    case "Careers":
      href = "https://storerouter.ai/careers";
      break;
    case "Accessibility":
      href = appendReturnTo(LEGAL_ACCESSIBILITY_PATH, LANDING_PAGE_PATH);
      break;
    default:
      href = null;
  }

  const isExternalLink = href?.startsWith("http");

  if (!href) {
    return (
      <div className="flex items-center justify-center px-2">
        <span className="text-button text-ink-faint text-center">{label}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-2">
      <a
        href={href}
        target={isExternalLink ? "_blank" : undefined}
        rel={isExternalLink ? "noreferrer" : undefined}
        className="text-button text-ink-faint cursor-pointer text-center transition-opacity hover:opacity-70"
      >
        {label}
      </a>
    </div>
  );
}
